import { useState, useEffect, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import { Button } from "@/components/ui/button";
import { X } from "lucide-react";
import { useSubscription } from "@/hooks/use-subscription";

interface TourStep {
  selector: string;
  title: string;
  description: string;
  position: "top" | "bottom" | "left" | "right";
}

const BASE_STEPS: TourStep[] = [
  {
    selector: "[data-tour='sidebar-nav']",
    title: "Navegação",
    description:
      "Use o menu lateral para acessar todas as seções do app: transações, planejamento, metas e mais.",
    position: "right",
  },
  {
    selector: "[data-tour='dashboard-overview']",
    title: "Visão Geral",
    description:
      "No Dashboard você acompanha saldos, receitas e despesas do período selecionado.",
    position: "bottom",
  },
  {
    selector: "[data-tour='add-transaction']",
    title: "Nova Transação",
    description:
      "Registre receitas e despesas rapidamente clicando neste botão.",
    position: "bottom",
  },
  {
    selector: "[data-tour='reports-section']",
    title: "Relatórios",
    description:
      "Visualize gráficos e relatórios detalhados para entender seus gastos.",
    position: "right",
  },
];

const FREEMIUM_STEP: TourStep = {
  selector: "[data-tour='upgrade-prompt']",
  title: "Recursos Premium",
  description:
    "Alguns recursos precisam de um plano pago. Experimente grátis por 7 dias!",
  position: "bottom",
};

interface ProductTourProps {
  onComplete: () => void;
}

interface HighlightRect {
  top: number;
  left: number;
  width: number;
  height: number;
}

function getElementRect(selector: string): HighlightRect | null {
  const el = document.querySelector(selector);
  if (!el) return null;
  const rect = el.getBoundingClientRect();
  return {
    top: rect.top + window.scrollY,
    left: rect.left + window.scrollX,
    width: rect.width,
    height: rect.height,
  };
}

function getTooltipStyle(
  rect: HighlightRect,
  position: TourStep["position"],
  tooltipWidth: number,
  tooltipHeight: number
): React.CSSProperties {
  const padding = 12;
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;
  const margin = 16; // minimum margin from viewport edges

  let top: number;
  let left: number;

  // On mobile (< 640px), always position tooltip below or above the element
  const isMobile = viewportWidth < 640;

  if (isMobile) {
    // On mobile, center horizontally and place below the element
    left = margin;
    top = rect.top + rect.height + padding;

    // If tooltip would go below viewport, place above instead
    if (top + tooltipHeight > viewportHeight + window.scrollY - margin) {
      top = rect.top - tooltipHeight - padding;
    }
  } else {
    switch (position) {
      case "right":
        top = rect.top + rect.height / 2 - tooltipHeight / 2;
        left = rect.left + rect.width + padding;
        break;
      case "left":
        top = rect.top + rect.height / 2 - tooltipHeight / 2;
        left = rect.left - tooltipWidth - padding;
        break;
      case "bottom":
        top = rect.top + rect.height + padding;
        left = rect.left + rect.width / 2 - tooltipWidth / 2;
        break;
      case "top":
        top = rect.top - tooltipHeight - padding;
        left = rect.left + rect.width / 2 - tooltipWidth / 2;
        break;
    }

    // Clamp to viewport bounds
    left = Math.max(margin, Math.min(left, viewportWidth - tooltipWidth - margin));
    top = Math.max(margin + window.scrollY, top);
  }

  return { position: "absolute", top, left };
}

export function ProductTour({ onComplete }: ProductTourProps) {
  const { isFreemium } = useSubscription();
  const [currentStep, setCurrentStep] = useState(0);
  const [highlightRect, setHighlightRect] = useState<HighlightRect | null>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const [tooltipStyle, setTooltipStyle] = useState<React.CSSProperties>({});

  const steps = isFreemium ? [...BASE_STEPS, FREEMIUM_STEP] : BASE_STEPS;
  const totalSteps = steps.length;
  const step = steps[currentStep];

  const updatePosition = useCallback(() => {
    if (!step) return;
    const rect = getElementRect(step.selector);

    if (rect) {
      setHighlightRect(rect);
      // Scroll element into view
      const el = document.querySelector(step.selector);
      el?.scrollIntoView({ behavior: "smooth", block: "center" });
    } else {
      // Element not found, use a centered fallback
      setHighlightRect(null);
    }
  }, [step]);

  useEffect(() => {
    updatePosition();
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);
    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [updatePosition]);

  // Compute tooltip position after render
  useEffect(() => {
    if (!highlightRect || !tooltipRef.current || !step) return;
    const tooltipRect = tooltipRef.current.getBoundingClientRect();
    const style = getTooltipStyle(
      highlightRect,
      step.position,
      tooltipRect.width,
      tooltipRect.height
    );
    setTooltipStyle(style);
  }, [highlightRect, step]);

  const handleNext = () => {
    if (currentStep < totalSteps - 1) {
      setCurrentStep((prev) => prev + 1);
    } else {
      onComplete();
    }
  };

  const handleSkip = () => {
    onComplete();
  };

  const overlayPadding = 8;

  return createPortal(
    <div className="fixed inset-0 z-[9999]" aria-modal="true" role="dialog">
      {/* Dark overlay with hole */}
      <svg
        className="absolute inset-0 w-full h-full"
        style={{ pointerEvents: "none" }}
      >
        <defs>
          <mask id="tour-mask">
            <rect x="0" y="0" width="100%" height="100%" fill="white" />
            {highlightRect && (
              <rect
                x={highlightRect.left - overlayPadding}
                y={highlightRect.top - overlayPadding}
                width={highlightRect.width + overlayPadding * 2}
                height={highlightRect.height + overlayPadding * 2}
                rx="8"
                fill="black"
              />
            )}
          </mask>
        </defs>
        <rect
          x="0"
          y="0"
          width="100%"
          height="100%"
          fill="rgba(0,0,0,0.6)"
          mask="url(#tour-mask)"
        />
      </svg>

      {/* Clickable overlay to prevent interaction */}
      <div
        className="absolute inset-0 z-[1]"
        onClick={(e) => e.stopPropagation()}
      />

      {/* Highlight ring */}
      {highlightRect && (
        <div
          className="absolute z-[2] border-2 border-primary rounded-lg pointer-events-none"
          style={{
            top: highlightRect.top - overlayPadding,
            left: highlightRect.left - overlayPadding,
            width: highlightRect.width + overlayPadding * 2,
            height: highlightRect.height + overlayPadding * 2,
          }}
        />
      )}

      {/* Tooltip */}
      <div
        ref={tooltipRef}
        className="absolute z-[10] w-[calc(100vw-2rem)] sm:w-80 bg-popover text-popover-foreground rounded-lg shadow-xl border p-4 pointer-events-auto"
        style={tooltipStyle}
      >
        <div className="flex items-start justify-between mb-2">
          <h3 className="font-semibold text-base">{step?.title}</h3>
          <button
            onClick={handleSkip}
            className="p-1 -mt-1 -mr-1 rounded-md hover:bg-muted transition-colors"
            aria-label="Fechar tour"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <p className="text-sm text-muted-foreground mb-4">
          {step?.description}
        </p>
        <div className="flex items-center justify-between">
          <span className="text-xs text-muted-foreground">
            {currentStep + 1}/{totalSteps}
          </span>
          <div className="flex gap-2">
            <Button variant="ghost" size="sm" onClick={handleSkip}>
              Pular tour
            </Button>
            <Button size="sm" onClick={handleNext}>
              {currentStep < totalSteps - 1 ? "Próximo" : "Concluir"}
            </Button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
